import type { QuerySpec } from '@console/types'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQueryExecution } from '../ask/queries.js'
import { decodeQuerySpec } from '../ask/result-id.js'
import {
  AggregateResultRenderer,
  BreakdownResultRenderer,
  TimelineResultRenderer,
} from '../ask/renderers.js'
import { AsOfLine, Button, Skeleton } from '../ui/index.js'
import styles from './AskResultPage.module.css'

/**
 * `/ask/result/<id>` (spec §7).
 *
 * Bookmarkable, shareable, refreshable: the id carries the query spec, so
 * loading this URL re-runs the query against current data and re-renders with
 * a fresh `as_of`. The model is not involved — a saved result URL never
 * re-invokes the classifier, which is why the same link always shows the same
 * *question* even as the *numbers* move.
 */

type Leaf = Exclude<QuerySpec, { kind: 'compare' }>

/** Dispatches one executed sub-result to the renderer for its kind. */
function LeafResult({ spec, result, asOf }: { spec: Leaf; result: unknown; asOf: string }) {
  switch (spec.kind) {
    case 'aggregate':
      return (
        <AggregateResultRenderer
          result={spec}
          buckets={(result as { buckets: never[] | null }).buckets}
          asOf={asOf}
        />
      )
    case 'breakdown':
      return (
        <BreakdownResultRenderer
          result={spec}
          rows={(result as { rows: never[] }).rows}
          asOf={asOf}
        />
      )
    case 'timeline':
      return (
        <TimelineResultRenderer
          result={spec}
          buckets={(result as { buckets: never[] }).buckets}
          asOf={asOf}
        />
      )
    case 'filter':
      // A filter result reaches the Runs page, never here (spec §5.1). If one
      // ever lands here it means the routing above changed and this is the
      // honest thing to say about it, rather than half-rendering run rows in
      // a shape this page doesn't own.
      return <p className={styles['note']}>This result is a run list — open it on the Runs page.</p>
  }
}

export function AskResultPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const question = searchParams.get('q')

  const spec = id === undefined ? null : decodeQuerySpec(id)
  const execution = useQueryExecution(spec)

  if (spec === null) {
    return (
      <main className={styles['page']}>
        <Link to="/ask" className={styles['home']}>
          ← Ask
        </Link>
        <h1>That link isn&apos;t a query</h1>
        <p className={styles['note']}>
          The result id in this URL couldn&apos;t be read back as a query. It may have been
          truncated or edited.
        </p>
        <Link to="/ask">
          <Button variant="primary">Ask a question</Button>
        </Link>
      </main>
    )
  }

  const executed = execution.data

  return (
    <main className={styles['page']}>
      <Link to="/ask" className={styles['home']}>
        ← Ask
      </Link>

      <div className={styles['header']}>
        <div>
          {/* The original question, so a bookmarked result still says what was asked. */}
          <h1 className={styles['question']}>{question ?? 'Result'}</h1>
          <p className={styles['label']}>{spec.kind === 'compare' ? 'side by side' : spec.kind}</p>
        </div>
        {executed && (
          <AsOfLine
            asOf={executed.as_of}
            onRefresh={() => {
              void execution.refetch()
            }}
          />
        )}
      </div>

      {execution.isPending && <Skeleton height="6rem" />}

      {execution.isError && (
        <p role="alert" className={styles['error']}>
          Could not run that query. {execution.error.message}
        </p>
      )}

      {executed &&
        (spec.kind === 'compare' ? (
          /**
           * Two or more results side by side and nothing else. No synthesized
           * comparison, ever — "X is 12% better than Y" is interpretation,
           * and the operator draws that conclusion themselves (spec §5.5).
           */
          <div className={spec.queries.length > 2 ? styles['compareWide'] : styles['compare']}>
            {spec.queries.map((sub, index) => (
              <LeafResult
                key={`${sub.kind}-${String(index)}`}
                spec={sub}
                result={(executed.data.result as { results: unknown[] }).results[index]}
                asOf={executed.as_of}
              />
            ))}
          </div>
        ) : (
          <LeafResult spec={spec} result={executed.data.result} asOf={executed.as_of} />
        ))}
    </main>
  )
}
